import { getBrowserFeatures } from './lib/get-browser.js';
import MobileDetect from 'mobile-detect';

/**
 * Store the oldie check value
 * @type {boolean}
 */
let oldBrowser;

/**
 * @callback isOldBrowser
 * @param {object} browserFeatures - The detected features of the current browser.
 * @returns {boolean} - Whether the browser should be considered 'old'.
 */

/**
 * Checks whether the current browser is considered 'old'
 * @param {object} configs
 * @param {string} configs.ie - Lowest InternetExplorer version
 * @param {string} configs.firefox - Lowest Firefox version
 * @param {string} configs.chrome - Lowest Chrome version
 * @param {string} configs.safari - Lowest Safari version
 * @param {isOldBrowser} [configs.isOldBrowser=null] - Optional function to check whether a browser is
 *  considered 'old'. If omitted, browsers whose versions are below the requirements or are insecure and do have updates
 *  available are treated as 'old'.
 * @returns {boolean}
 */
export function oldBrowserCheck(configs) {
    // return cached browser if available
    if (oldBrowser !== undefined) {
        return oldBrowser;
    }

    let doc = document.documentElement,
        requirements = {
            i: configs.ie,
            f: configs.firefox,
            c: configs.chrome,
            s: configs.safari,
        },
        browserFeatures = getBrowserFeatures(requirements),
        browser = browserFeatures.browser,
        mobileDetect = new MobileDetect(navigator.userAgent);

    if ('isOldBrowser' in configs && typeof configs.isOldBrowser === 'function') {
        oldBrowser = configs.isOldBrowser(browserFeatures);
    } else {
        oldBrowser = browser.no_device_update !== true && (browserFeatures.is_below_required || browser.is_insecure);
    }

    // add the oldie class to the html element when we have an old browser
    if (oldBrowser) {
        doc.classList.add('oldie');
    }

    // store the value in window.oldBrowser
    window.oldie = oldBrowser;

    // ===================================================================
    //                     browser detection helper
    // ===================================================================

    // add the user agent as data attribute
    doc.dataset.useragent = navigator.userAgent;

    // if the user in in an application (like facebook "in app browser")
    // http://stackoverflow.com/questions/31569518/how-to-detect-facebook-in-app-browser#32348687
    doc.dataset.app = navigator.userAgent.indexOf('FBAN') > -1 || navigator.userAgent.indexOf('FBAV') > -1 ? 'fb' : '';

    // add the browser abbreviation as data attribute
    doc.dataset.browser = browser.n;

    switch (browser.n) {
        case 'ios': // on iOS, all browser use the same webview underneath
            doc.dataset.ios = browser.v;
            break;

        case 's': // Safari desktop
            doc.dataset.safari = browser.v;
            break;

        case 'i': // IE
            doc.dataset.ie = browser.v;
            break;

        case 'c': // Chrome
            doc.dataset.chrome = browser.v;
            break;

        case 'f': // Firefox
            doc.dataset.ff = browser.v;
            break;

        default:
            break;
    }

    doc.dataset.mobile = mobileDetect.mobile() !== null;
    doc.dataset.tablet = mobileDetect.tablet() !== null;

    return oldBrowser;
}
